import { usePlayerStore } from '@/store/playerStore';
import { audioEngine } from '@/features/audio/audioEngine';
import { IconButton } from '@/components/ui/IconButton';
import { EqualizerBars } from './EqualizerBars';
import { SmartTuneControl } from '@/components/smart-tune/SmartTuneControl';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Heart,
  RotateCcw,
  SkipBack,
  ExternalLink,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useFavoritesStore } from '@/store/userStore';
import { useUIStore } from '@/store/uiStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { clsx } from 'clsx';
import { PRODUCT } from '@/config/product.config';

export function PlayerBar() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const status = usePlayerStore((s) => s.status);
  const station = usePlayerStore((s) => s.currentStation);
  const isMuted = usePlayerStore((s) => s.isMuted);
  const volume = usePlayerStore((s) => s.volume);
  const error = usePlayerStore((s) => s.errorMessage);
  const hasPrev = usePlayerStore((s) => !!s.previousStation);
  const isFav = useFavoritesStore((s) => (station ? s.isFavorite(station.id) : false));
  const toggleFav = useFavoritesStore((s) => s.toggle);
  const setPlayerExpanded = useUIStore((s) => s.setPlayerExpanded);

  const isLoading = status === 'loading';
  const isBuffering = status === 'buffering';
  const isPlaying = status === 'playing';
  const isError = status === 'error';
  const isBlocked = status === 'blocked';
  const showSpinner = isLoading || isBuffering;

  return (
    <footer
      className="absolute inset-x-0 bottom-0 z-30"
      role="region"
      aria-label="Oynatıcı"
    >
      <div className="mx-auto mb-3 flex items-center gap-3 px-3 py-2.5 glass-strong rounded-2xl shadow-glow sm:mx-4 lg:mb-4 lg:mx-auto lg:max-w-3xl">
        {/* Station info / empty */}
        <button
          type="button"
          onClick={() => station && setPlayerExpanded(true)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          disabled={!station}
        >
          <span
            className={clsx(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-500/15 text-accent-300',
              isPlaying && 'is-playing',
            )}
          >
            {showSpinner ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : isError ? (
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            ) : (
              <EqualizerBars active={isPlaying} />
            )}
          </span>
          <span className="min-w-0">
            {station ? (
              <>
                <span className="block truncate text-sm font-semibold text-slate-100">
                  {station.name}
                </span>
                <span className="block truncate text-xs text-slate-400">
                  {station.cityName ? `${station.cityName}, ` : ''}
                  {station.countryName ?? ''}
                  {station.streams[0] && (
                    <span className="ml-1 font-mono text-slate-500">
                      · {station.streams[0].codec} {station.streams[0].bitrate}k
                    </span>
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="block truncate text-sm font-semibold text-slate-300">
                  {PRODUCT.name}
                </span>
                <span className="block truncate text-xs text-slate-500">
                  Bir istasyon seç ve dinlemeye başla
                </span>
              </>
            )}
          </span>
        </button>

        {/* Controls */}
        <div className="flex items-center gap-1">
          {isDesktop && hasPrev && (
            <IconButton label="Önceki istasyon" onClick={() => audioEngine.previous()}>
              <SkipBack className="h-4 w-4" />
            </IconButton>
          )}

          {isBlocked ? (
            <button
              type="button"
              onClick={() => audioEngine.play()}
              className="flex items-center gap-1.5 rounded-lg bg-accent-500 px-3 py-2 text-sm font-semibold text-navy-950 hover:bg-accent-400"
            >
              <Play className="h-4 w-4" /> Dinlemeyi Başlat
            </button>
          ) : isError ? (
            <IconButton label="Tekrar dene" variant="primary" onClick={() => audioEngine.retry()}>
              <RotateCcw className="h-4 w-4" />
            </IconButton>
          ) : (
            <IconButton
              label={isPlaying ? 'Duraklat' : 'Oynat'}
              variant="primary"
              onClick={() => (isPlaying ? audioEngine.pause() : audioEngine.play())}
              disabled={!station}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </IconButton>
          )}

          {isDesktop && station?.homepageUrl && (
            <a
              href={station.homepageUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Web sitesini aç"
              title="Web sitesini aç"
              className="hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 text-slate-200 hover:bg-white/10"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}

          {isDesktop && (
            <IconButton
              label={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
              variant={isFav ? 'primary' : 'ghost'}
              onClick={() => station && toggleFav(station.id)}
              disabled={!station}
            >
              <Heart className={clsx('h-4 w-4', isFav && 'fill-current')} />
            </IconButton>
          )}

          {isDesktop && <SmartTuneControl />}

          {/* Volume */}
          {isDesktop && (
            <div className="ml-1 flex items-center gap-2">
              <IconButton label={isMuted ? 'Sesi aç' : 'Sesi kapat'} onClick={() => audioEngine.toggleMute()}>
                {isMuted || volume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </IconButton>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(e) => audioEngine.setVolume(Number(e.target.value))}
                aria-label="Ses seviyesi"
                className="h-1 w-20 accent-accent-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Error message line */}
      {error && (
        <div className="mb-1 px-4 text-center text-xs text-orange-400">
          {error}
        </div>
      )}
    </footer>
  );
}
